import clsx from 'clsx';
import { getScaleArray, getScaleSlot, lengthByScoreScale, parseGradesToStandardScale } from 'core/utils/parses';
import { getStudentDisplayName } from 'core/utils/user';
import { flatten } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Avatar, Icon } from 'ui';
import StudentsGradesDropdownMenu from '../dropdowns/StudentsGradesDropdownMenu/StudentsGradesDropdownMenu';
import { getGradeDistributionItems } from './studentsGradeDistribution';

// Referencia estable: students es dependencia del efecto que agrupa por nota
const NO_STUDENTS = [];

// La lista de la lección pasa alumnos con `guid`; Resultados y Mint, con `student_guid`.
function getStudentKey(student) {
  return student.student_guid ?? student.guid;
}

const VerticalStudentsResults = ({
  students = NO_STUDENTS,
  t,
  showNotTurned = true,
  showNotEvaluated = true,
  withColorScale = false,
  scoreScale,
  evaluationScale,
}) => {
  const barRef = useRef(null);
  const [groupedStudents, setGroupedStudents] = React.useState([]);
  const [openDropdownStudents, setOpenDropdownStudents] = React.useState(false);
  const [maxRepeatValue, setMaxRepeatValue] = React.useState(0);
  const [allStudents, setAllStudents] = React.useState(students);
  const [anchorEl, setAnchorEl] = React.useState(barRef ? barRef.current : {});
  const [studentsToDropdown, setStudentsToDropdown] = React.useState([]);

  useEffect(() => {
    setAllStudents(students);
    groupStudentsByScore(students, scoreScale);
  }, [students, scoreScale]);

  function groupStudentsByScore(_allStudents, scoreScale) {
    if (_allStudents) {
      let filterStudents = _allStudents.filter((item) => item.turned);
      if (!showNotEvaluated) filterStudents = filterStudents.filter((item) => item.evaluated);
      let result = {};
      result = filterStudents.reduce(function (r, a) {
        r[parseInt(a.score)] = r[a.score] || [];
        r[parseInt(a.score)].push(a);
        return r;
      }, Object.create(null));

      result = parseGroupStudentsToScoreScale(result, scoreScale);
      result.notturned = _allStudents.filter((item) => !item.turned);

      getMaxValueRepeat(result);
      setGroupedStudents(result);
    }
  }

  function parseGroupStudentsToScoreScale(studentsByScore, scoreScale) {
    let newObject = {};
    let scaleArray = getScaleArray(scoreScale);
    scaleArray.forEach((scale) => (newObject[scale] = []));

    Object.entries(studentsByScore).forEach(([key, value]) => {
      let newKey = key !== 'notturned' ? getScaleSlot(key, scoreScale) : key;
      newObject[newKey] = newObject[newKey] || [];
      newObject[newKey].push(value);
      newObject[newKey] = flatten(newObject[newKey]);
    });

    return newObject;
  }

  function getMaxValueRepeat(result) {
    let maxValue = 0;
    for (let scoresStudents in result) {
      if (scoresStudents !== 'notturned') {
        maxValue = result[scoresStudents].length > maxValue ? result[scoresStudents].length : maxValue;
      }
    }
    setMaxRepeatValue(maxValue);
  }

  function onOpenDropdownStudents(e, studentsScore) {
    e.preventDefault();
    e.stopPropagation();
    setStudentsToDropdown(studentsScore);
    setAnchorEl(e.currentTarget);
    setOpenDropdownStudents(true);
  }

  function onCloseDropdownStudents() {
    setOpenDropdownStudents(false);
  }

  function setBarHeight(size) {
    if (size) {
      return (size * 100) / maxRepeatValue;
    } else {
      return 0;
    }
  }

  function rendeNumberBubble(count) {
    return (
      <div aria-hidden="true" className="vertical-students-results__count-bubble">
        <span className="vertical-students-results__count-bubble-text">{count}</span>
      </div>
    );
  }

  function renderAvatars(allStudents = [], score) {
    return (
      <div className="vertical-students-results__avatars-container">
        <div className={`vertical-students-results__avatars vertical-students-results__avatars--${allStudents.length <= 2 ? allStudents.length : 'more'}`}>
          {allStudents.slice(0, 2).map((item) => (
            <button
              // El ancla es el avatar pulsado: su guid dice cuál de los dos tiene abierta la lista
              aria-expanded={openDropdownStudents && anchorEl?.dataset?.studentGuid === getStudentKey(item)}
              aria-label={getStudentDisplayName(item.name, item.lastName, false, item.lastname)}
              className="horizontal-students-result__student"
              data-student-guid={getStudentKey(item)}
              key={getStudentKey(item)}
              onClick={(e) => onOpenDropdownStudents(e, groupedStudents[score])}
              ref={barRef}
              type="button"
            >
              <Avatar image={item.thumbnail} size="md" />
            </button>
          ))}
          {allStudents.length > 2 && rendeNumberBubble(allStudents.length)}
        </div>
        <div className="vertical-students-results__arrow">
          <Icon name="arrow-down" />
        </div>
      </div>
    );
  }

  function renderBar(score, fillPercent, text) {
    return (
      <div className={`vertical-students-results__bar ${score === 'notturned' ? 'vertical-students-results__bar--notturned' : ''}`} key={text}>
        <div className="vertical-students-results__bar-container">
          <div
            className={`vertical-students-results__bar-progress-container ${
              score === 'notturned' && fillPercent === 0 ? 'vertical-students-results__bar-progress-container--notturned-all-turned' : ''
            }`}
          >
            {' '}
            <div
              className={clsx(
                'vertical-students-results__bar-progress',
                {
                  'vertical-students-results__bar-progress--bad': parseGradesToStandardScale(score, scoreScale) < 5 && withColorScale,
                },
                {
                  'vertical-students-results__bar-progress--good': parseGradesToStandardScale(score, scoreScale) >= 5 && withColorScale,
                }
              )}
              style={{ height: `${fillPercent}%` }}
            />
          </div>

          {fillPercent > 0 && (
            <div className="vertical-students-results__bar-students" style={{ bottom: `${fillPercent + 4}%` }}>
              {renderAvatars(groupedStudents[score], score)}
            </div>
          )}
        </div>
        <div
          // La nota de cada barra ya la da la lista de la distribución; el porcentaje de no entregados no
          aria-hidden={score === 'notturned' ? undefined : true}
          className={`vertical-students-results__score ${score === 'notturned' ? 'vertical-students-results__score--notturned' : ''} ${
            score === 'notturned' && fillPercent === 0 ? 'vertical-students-results__score--notturned-all-turned' : ''
          }`}
        >
          {text}
        </div>
        {score === 'notturned' && fillPercent !== 0 && <div className="vertical-students-results__score-percent">{t('Not turned it')}</div>}
        {score === 'notturned' && fillPercent === 0 && <div className="vertical-students-results__score-percent">{t('Every one turned it')}</div>}
      </div>
    );
  }

  return (
    <div className="vertical-students-results">
      <ul className="sr-only">
        {getGradeDistributionItems(groupedStudents, scoreScale, t).map(({ grade, label }) => (
          <li key={grade}>{label}</li>
        ))}
      </ul>
      <div className="vertical-students-results__bars-wrapper">
        {showNotTurned && (
          <div className="vertical-students-results__bar-not-turned">
            {renderBar(
              'notturned',
              (100 * allStudents.filter((item) => !item.turned).length) / allStudents.length,
              (100 * allStudents.filter((item) => !item.turned).length) / allStudents.length === 0
                ? '100%'
                : (100 * allStudents.filter((item) => !item.turned).length) / allStudents.length
                ? ((100 * allStudents.filter((item) => !item.turned).length) / allStudents.length).toFixed(1) + '%'
                : ''
            )}
          </div>
        )}

        {Array(lengthByScoreScale(scoreScale))
          .fill()
          .map(
            (_, i) =>
              Object.keys(groupedStudents)[i] !== 'notturned' &&
              renderBar(
                Object.keys(groupedStudents)[i],
                Object.values(groupedStudents)[i] ? setBarHeight(Object.values(groupedStudents)[i].length) : 0,
                getScaleArray(scoreScale)[i]
              )
          )}
      </div>
      <div className="vertical-students-results_info" />

      <StudentsGradesDropdownMenu
        users={studentsToDropdown}
        open={openDropdownStudents}
        onClose={onCloseDropdownStudents}
        anchorEl={anchorEl || barRef.current}
        scoreScale={scoreScale}
        evaluationScale={evaluationScale}
      />
    </div>
  );
};

VerticalStudentsResults.propTypes = {
  evaluationScale: PropTypes.object,
  scoreScale: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showNotEvaluated: PropTypes.bool,
  showNotTurned: PropTypes.bool,
  students: PropTypes.array,
  t: PropTypes.func,
  withColorScale: PropTypes.bool,
};

export default withTranslation(['lesson'])(VerticalStudentsResults);
